// ONE ARKit-52 frame per render frame for a splat character: shared precedence, splat passes.
// Notes: ~/comments/aos-threejs-poc/arkitFrame_composeSplatArkitFrame_shared_precedence.md

import { selectFaceArkitFrame, ARKIT_LEN } from "../../lib/faceFrame.js";
import { maskLiveArkitRegions, maskArkitRegions } from "../rig/liveRigRegions.js";
import { ARKIT_NAMES } from "../../lib/arkitNames.js";

const BLINK_LEFT = ARKIT_NAMES.indexOf("EyeBlinkLeft");
const BLINK_RIGHT = ARKIT_NAMES.indexOf("EyeBlinkRight");
if (BLINK_LEFT < 0 || BLINK_RIGHT < 0) throw new Error("[arkitFrame] ARKIT_NAMES has no EyeBlinkLeft/Right");

export type FrameOwner = "forced" | "rig" | "track" | "live" | "clips" | "idle";

/** The sources the shared resolver picks between — faceFrame.js owns the precedence. */
export interface FrameSources {
  forcedArkit?: ArrayLike<number> | null;
  rigWeight?: number;
  rigArkit?: ArrayLike<number> | null;
  /** Webcam Face Tracker node (ENG-1107) — faceTrackWeight() and faceTrackState.arkit. */
  trackWeight?: number;
  trackArkit?: ArrayLike<number> | null;
  liveWeight?: number;
  liveArkit?: ArrayLike<number> | null;
  clipWeights?: ArrayLike<number> | null;
}

/** The splat-only stages, in the order they must run. */
export interface FramePasses {
  /** Zero the A2F eye/brow/cheek channels while SPEAKING — measured noise, not signal. */
  regionMask?: boolean;
  /** Channel groups this bundle declines (scene.json face_drives); absent = drive it. */
  regions?: Partial<Record<"brow" | "eye" | "cheek" | "nose", boolean>> | null;
  /** Procedural blink envelope, 0 for none. Applied AFTER the masks, so a blink survives them. */
  blink?: number;
  /** Eye-follow aim overlay: EyeLook channels, others 0. */
  eyeOverlay?: ArrayLike<number> | null;
  /** The Anim Test Panel's kill switch. False holds one neutral frame. */
  faceAnim?: boolean;
}

export interface ComposedFrame {
  owner: FrameOwner;
  /** True when the live A2F eye/brow/cheek channels were held at rest this frame. */
  liveMasked: boolean;
}

/**
 * Compose `out` (52 floats, overwritten). Allocation-free.
 *
 * The OWNER comes from the shared resolver so a splat head and a morph head can never
 * disagree about who is speaking. The overlays are applied HERE rather than there,
 * because the splat's masks must run between the owner and the blink.
 */
export function composeSplatArkitFrame(sources: FrameSources, passes: FramePasses, out: Float32Array): ComposedFrame {
  // Blink and the eye overlay are withheld from the shared resolver on purpose: it
  // applies them immediately, and the masks below would then erase them.
  const owner = selectFaceArkitFrame(
    { ...sources, blink: 0, eyeOverlay: null },
    out,
  ) as FrameOwner;

  // Live speech only: face clips and Rig Preview frames are AUTHORED, eye channels included.
  const liveMasked = owner === "live" && passes.regionMask !== false;
  if (liveMasked) maskLiveArkitRegions(out);

  // Regions this bundle's capture cannot take, held at REST. Before the blink, so a
  // procedural blink still reaches the rig even on a bundle that declines the eye drive.
  if (passes.regions) maskArkitRegions(out, passes.regions);

  const blink = passes.blink ?? 0;
  if (blink > 0) {
    if (blink > out[BLINK_LEFT]) out[BLINK_LEFT] = Math.min(1, out[BLINK_LEFT] + blink);
    if (blink > out[BLINK_RIGHT]) out[BLINK_RIGHT] = Math.min(1, out[BLINK_RIGHT] + blink);
  }
  const overlay = passes.eyeOverlay;
  if (overlay) for (let i = 0; i < ARKIT_LEN; i++) if (overlay[i] > out[i]) out[i] = overlay[i];

  // Last: OFF is one neutral frame, not a frozen old one.
  if (passes.faceAnim === false) out.fill(0);

  return { owner, liveMasked };
}

export { ARKIT_LEN, BLINK_LEFT, BLINK_RIGHT };
